import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { ActionIcon, Alert, Button, Group, Loader, Pagination, Select, Text, TextInput, Tooltip } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconRefresh, IconSearch } from "@tabler/icons-react";
import { InstantSearchProps, useHitsPerPage, useInstantSearch, usePagination, useSearchBox } from "react-instantsearch";
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import { MyUserDataContext } from "../../contexts";
import { CtcPurchaseOrder, CtcSettlement, CtcSettlementSide } from "../../types";
import { CTC_PO_STATUS_TAGS, CtcPoStatusCode } from "../../../../shared/ctcLifecycle";
import { listMemory } from "./ctc_orders_table";
import { ctcClearable } from "./ctc_panel";
import { useFirestore } from "../../helpers/firebaseContext";
import { useCtcUser } from "./ctc_helpers";
import { CTC_SEARCH_UPDATES, CtcSearchKind } from "../../../../shared/ctcSearch";

/*
 * Typesense pieces shared by the CTC lists (ctc_orders_search.tsx, ctc_transactions.tsx), the same stack as
 * routes/orders (react-instantsearch + typesense-instantsearch-adapter). Documents: functions-ctc/src/search.ts.
 * The search key (customClaims → ctcSearchKey) limits bank users to their own customer.
 */

export const CTC_ORDERS_INDEX = import.meta.env.VITE_CTC_TYPESENSE_ORDERS_COLLECTION as string;
export const CTC_SETTLEMENTS_INDEX = import.meta.env.VITE_CTC_TYPESENSE_SETTLEMENTS_COLLECTION as string;
export const CTC_PAGE_SIZES = [25, 50, 100, 250];

/**
 * Search client for one CTC collection; `queryBy` = the fields the search box looks in. They are infix fields
 * (functions-ctc/src/search.ts), so any part matches: "01124" or "124" finds KFH-DSD-26-01124.
 */
export const useCtcSearchClient = (queryBy: string) => {
    const { ctcSearchKey } = useContext(MyUserDataContext);
    return useMemo(() => new TypesenseInstantSearchAdapter({
        server: {
            apiKey: ctcSearchKey ?? "",
            // Same cluster as functions-ctc's CTC_TYPESENSE_HOST (falls back to the portal's Typesense host)
            nodes: [{ host: import.meta.env.VITE_CTC_TYPESENSE_HOST || import.meta.env.VITE_TYPESENSE_SEARCH_HOST!, port: 443, protocol: "https" }],
        },
        additionalSearchParameters: { query_by: queryBy, infix: queryBy.split(",").map(() => "always").join(",") },
    }).searchClient, [ctcSearchKey, queryBy]);
};

/**
 * The list's search box, page and page size are kept while the app is open (coming back from an order
 * shows the same page). The page is dropped when the sort (= index) changes.
 */
export const useCtcSearchMemory = (list: string, indexName: string) => {
    const key = `ts:${list}:ui`;
    const saved = listMemory.get(key) as { index: string; state: Record<string, unknown> } | undefined;
    const state = saved ? (saved.index === indexName ? saved.state : { ...saved.state, page: undefined }) : {};
    const initialUiState = { [indexName]: state } as InstantSearchProps["initialUiState"];
    const onStateChange: InstantSearchProps["onStateChange"] = ({ uiState, setUiState }) => {
        setUiState(uiState);
        listMemory.set(key, { index: indexName, state: uiState[indexName] ?? {} });
    };
    return { initialUiState, onStateChange };
};

/** Search box (debounced); it looks in the fields of the list's search client. */
export function CtcSearchInput({ placeholder, w = 260 }: { placeholder: string; w?: number }) {
    const { query, refine } = useSearchBox();
    const [value, setValue] = useState(query);
    useEffect(() => {
        if (value === query) return;
        const t = setTimeout(() => refine(value.trim()), 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return (
        <TextInput leftSection={<IconSearch size={14} />} placeholder={placeholder} value={value} onChange={(e) => setValue(e.currentTarget.value)} w={w}
            {...ctcClearable(value, () => { setValue(""); refine(""); })} />
    );
}

export type CtcDayRange = [string | null, string | null];

/** Created between two Kuwait days ("YYYY-MM-DD"); either end may be empty. */
export function CtcCreatedRange({ value, onChange }: { value: CtcDayRange; onChange: (v: CtcDayRange) => void }) {
    return (
        <DatePickerInput type="range" allowSingleDateInRange clearable placeholder="Created: any date" value={value}
            onChange={(v) => onChange([v[0] ?? null, v[1] ?? null])} w={220} maxDate={new Date()} valueFormat="DD/MM/YYYY" />
    );
}

export function CtcSearchRefresh() {
    const { refresh, status } = useInstantSearch();
    return (
        <Tooltip label="Refresh">
            <ActionIcon variant="default" size="lg" loading={status === "stalled"} onClick={refresh} aria-label="Refresh"><IconRefresh size={16} /></ActionIcon>
        </Tooltip>
    );
}

/**
 * Makes a Typesense list live: functions-ctc rings this list's bell (ctc_search_updates) once Typesense has a
 * change, from anyone (portal, back office, automation, WhatsApp), and the list searches again. Desk staff hear
 * every change, a bank user only their own customer's.
 */
export function CtcSearchLiveRefresh({ kind }: { kind: CtcSearchKind }) {
    const db = useFirestore();
    const user = useCtcUser();
    const { refresh } = useInstantSearch();
    const refreshRef = useRef(refresh);
    refreshRef.current = refresh;
    const bell = user.isDesk ? kind : user.customerId ? `${kind}_${user.customerId}` : null;
    useEffect(() => {
        if (!bell) return;
        let first = true;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const stop = onSnapshot(doc(db, CTC_SEARCH_UPDATES, bell), () => {
            if (first) { first = false; return; } // the current state, not a change
            clearTimeout(timer);
            timer = setTimeout(() => refreshRef.current(), 400); // several changes at once → one search
        }, () => { /* can't listen: the list still updates on Refresh */ });
        return () => { stop(); clearTimeout(timer); };
    }, [db, bell]);
    return null;
}

/** Searches again every `seconds` while the list is open (Typesense lists aren't live). */
export function CtcSearchAutoRefresh({ seconds }: { seconds: number }) {
    const { refresh } = useInstantSearch();
    useEffect(() => {
        const t = setInterval(refresh, seconds * 1000);
        return () => clearInterval(t);
    }, [refresh, seconds]);
    return null;
}

/** Gives the parent the search's refresh (e.g. to search again after marking a commission settled). */
export function CtcSearchRefreshHandle({ onReady }: { onReady: (refresh: () => void) => void }) {
    const { refresh } = useInstantSearch();
    useEffect(() => onReady(refresh), [refresh, onReady]);
    return null;
}

/** Results count, page size (25 / 50 / 100 / 250) and pages. */
export function CtcSearchPager() {
    const { currentRefinement, nbPages, refine } = usePagination();
    const { items, refine: setSize } = useHitsPerPage({ items: CTC_PAGE_SIZES.map((n, i) => ({ label: String(n), value: n, default: i === 0 })) });
    const { results, status } = useInstantSearch();
    const size = items.find((i) => i.isRefined)?.value ?? CTC_PAGE_SIZES[0];
    const total = results?.nbHits ?? 0;
    const from = total ? currentRefinement * size + 1 : 0;
    const to = Math.min(total, (currentRefinement + 1) * size);
    return (
        <Group justify="space-between" mt="sm">
            <Group gap="xs">
                {status === "loading" || status === "stalled" ? <Loader size="xs" /> : null}
                <Text size="xs" c="dimmed">{total ? `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}` : "0"}</Text>
                <Select size="xs" w={80} data={CTC_PAGE_SIZES.map(String)} value={String(size)} onChange={(v) => v && setSize(Number(v))} allowDeselect={false} aria-label="Rows per page" />
                <Text size="xs" c="dimmed">per page</Text>
            </Group>
            {nbPages > 1 && <Pagination size="sm" total={nbPages} value={currentRefinement + 1} onChange={(p) => refine(p - 1)} />}
        </Group>
    );
}

/** Typesense failed (e.g. collection not created yet on this project): say so and offer Firestore. */
export function CtcSearchError({ onUseFirestore }: { onUseFirestore: () => void }) {
    const { error } = useInstantSearch({ catchError: true });
    if (!error) return null;
    return (
        <Alert color="red" mb="sm" title="Typesense search failed">
            <Text size="sm" mb="xs">{error.message}</Text>
            <Button size="compact-sm" variant="light" onClick={onUseFirestore}>Use Firestore</Button>
        </Alert>
    );
}

/* ---------- Typesense hits → the shapes the tables already show ---------- */

type Hit = Record<string, unknown>;
const stamp = (seconds: unknown) => typeof seconds === "number" ? { toDate: () => new Date(seconds * 1000), toMillis: () => seconds * 1000 } : undefined;
const text = (v: unknown) => (typeof v === "string" && v ? v : undefined);

export const ctcOrderFromHit = (h: Hit): CtcPurchaseOrder => ({
    id: h.id as string,
    referenceNumber: h.referenceNumber as string,
    clientRefNo: text(h.clientRefNo),
    customerId: h.customerId as string,
    beneficiary: { number: text(h.beneficiaryNumber) ?? "", mobile_code: text(h.beneficiaryMobileCode), mobile: text(h.beneficiaryMobile) },
    amount: h.amount as number,
    currencyId: h.currencyId as string,
    tags: { poStatus: CTC_PO_STATUS_TAGS[h.status as CtcPoStatusCode] ?? { code: h.status, title: String(h.status ?? ""), badgeColor: "gray" } },
    state: text(h.state),
    lane: text(h.lane),
    assignedDealerEmail: text(h.assignedDealerEmail),
    makerEmail: text(h.makerEmail),
    makerName: text(h.makerName),
    customerName: text(h.customerName),
    commissionName: text(h.commissionName),
    brokerName: text(h.brokerName),
    commodityName: text(h.commodityName),
    acceptedByEmail: text(h.acceptedByEmail),
    acceptedByName: text(h.acceptedByName),
    soldMethod: text(h.soldMethod),
    valueDate: text(h.valueDate),
    updatedBy: text(h.updatedBy),
    whatsappPrompt: typeof h.whatsappPromptSentAt === "number" ? { sentAt: stamp(h.whatsappPromptSentAt) } : undefined,
    pendingEffects: Array.from({ length: Number(h.pendingEffectsCount) || 0 }, () => "pending"),
    created: stamp(h.created),
    updated: stamp(h.updated),
} as unknown as CtcPurchaseOrder);

const sideFromHit = (h: Hit, prefix: "customer" | "broker"): CtcSettlementSide | undefined => {
    const status = h[`${prefix}Status`];
    if (typeof status !== "string") return undefined;
    return {
        status: status as CtcSettlementSide["status"],
        amount: typeof h[`${prefix}Amount`] === "number" ? h[`${prefix}Amount`] as number : undefined,
        currencyId: text(h[`${prefix}Currency`]),
        reference: text(h[`${prefix}Reference`]),
        comment: text(h[`${prefix}Comment`]),
        settledAt: stamp(h[`${prefix}SettledAt`]),
        settledBy: text(h[`${prefix}SettledBy`]),
    } as CtcSettlementSide;
};

export const ctcSettlementFromHit = (h: Hit): CtcSettlement => ({
    id: h.id as string,
    poId: (h.poId as string) ?? (h.id as string),
    referenceNumber: h.referenceNumber as string,
    customerId: h.customerId as string,
    settled: h.settled === true,
    deleted: h.deleted === true,
    orderState: text(h.orderState),
    customer: sideFromHit(h, "customer"),
    broker: sideFromHit(h, "broker"),
    created: stamp(h.created),
    updated: stamp(h.updated),
} as unknown as CtcSettlement);
